import { Box, Typography } from "@mui/material"
import type { FC } from "react"

import type { TransferSearchParams } from "../../schemas"

import { FilterChip } from "@/components/common/Filters"
import type { TransferStatus, TransferType } from "@/graphql/codegen/graphql"
import { InventoryFilterChipGroup } from "@/screens/Inventory/components"
import type { Option } from "@/types/form-fields"
import { formatDate } from "@/utils/date"

interface TransferFilterChipsProps {
  searchParams: TransferSearchParams
  typeOptions: Option<TransferType>[]
  statusOptions: Option<TransferStatus>[]
  warehouseOptions: Option<string>[]
  createdByOptions: Option<string>[]
  onRemove: (key: keyof TransferSearchParams, value?: string | number) => void
}

export const TransferFilterChips: FC<TransferFilterChipsProps> = ({
  searchParams,
  typeOptions,
  statusOptions,
  warehouseOptions,
  createdByOptions,
  onRemove,
}) => {
  const types = searchParams.types ?? []
  const statuses = searchParams.statuses ?? []
  const sourceWarehouseAreaIds = searchParams.sourceWarehouseAreaIds ?? []
  const targetWarehouseAreaIds = searchParams.targetWarehouseAreaIds ?? []
  const createdByUserIds = searchParams.createdByUserIds ?? []
  const { createdOnFrom, createdOnTo } = searchParams

  const hasActiveFilters =
    !!createdOnFrom ||
    !!createdOnTo ||
    types.length > 0 ||
    statuses.length > 0 ||
    sourceWarehouseAreaIds.length > 0 ||
    targetWarehouseAreaIds.length > 0 ||
    createdByUserIds.length > 0

  if (!hasActiveFilters) return null

  return (
    <Box className='flex flex-wrap items-center gap-2' data-testid='transfer.filter-chips'>
      <Typography variant='body1' className='text-gray-700'>
        Applied filters:
      </Typography>

      <InventoryFilterChipGroup
        label='Type'
        values={types}
        options={typeOptions}
        onRemove={(value) => onRemove("types", value)}
        testId='transfer.filter-chips.type'
      />

      <InventoryFilterChipGroup
        label='From Warehouse'
        values={sourceWarehouseAreaIds}
        options={warehouseOptions}
        onRemove={(value) => onRemove("sourceWarehouseAreaIds", value)}
        testId='transfer.filter-chips.from-warehouse'
      />

      <InventoryFilterChipGroup
        label='To Warehouse'
        values={targetWarehouseAreaIds}
        options={warehouseOptions}
        onRemove={(value) => onRemove("targetWarehouseAreaIds", value)}
        testId='transfer.filter-chips.to-warehouse'
      />

      {createdOnFrom && (
        <FilterChip
          label={`Created From: ${formatDate(createdOnFrom)}`}
          onDelete={() => onRemove("createdOnFrom")}
          testId='transfer.filter-chips.created-from'
        />
      )}

      {createdOnTo && (
        <FilterChip
          label={`Created To: ${formatDate(createdOnTo)}`}
          onDelete={() => onRemove("createdOnTo")}
          testId='transfer.filter-chips.created-to'
        />
      )}

      <InventoryFilterChipGroup
        label='Status'
        values={statuses}
        options={statusOptions}
        onRemove={(value) => onRemove("statuses", value)}
        testId='transfer.filter-chips.status'
      />

      <InventoryFilterChipGroup
        label='Created By'
        values={createdByUserIds}
        options={createdByOptions}
        onRemove={(value) => onRemove("createdByUserIds", value)}
        testId='transfer.filter-chips.created-by'
      />
    </Box>
  )
}
